import React, { useEffect, useState } from 'react';
import { Edge, EdgeMarker, MarkerType } from 'reactflow';
import { X, Trash2, Play, Pause } from 'lucide-react';
import { RangeSlider, Tooltip } from '@/components/ui/beui';

interface EdgeSettingsPanelProps {
  edge: Edge | null;
  onUpdate: (id: string, updates: Partial<Edge>) => void;
  onDelete: (id: string) => void;
  onClose: () => void;
}

const COLORS = [
  { label: 'Gray', value: '#64748b' },
  { label: 'Blue', value: '#3b82f6' },
  { label: 'Green', value: '#22c55e' },
  { label: 'Red', value: '#ef4444' },
  { label: 'Purple', value: '#a855f7' },
  { label: 'Orange', value: '#f97316' },
];

const STYLES = [
  { label: 'Solid', value: '0' },
  { label: 'Dashed', value: '5,5' },
  { label: 'Dotted', value: '2,2' },
];

// ReactFlow built-in edge path shapes, with a little preview path for each.
const TYPES = [
  { label: 'Smooth', value: 'smoothstep', d: 'M3 9 L8 9 Q10 9 10 7 L10 5 Q10 3 12 3 L19 3' },
  { label: 'Curved', value: 'default', d: 'M3 9 C 9 9 13 3 19 3' },
  { label: 'Step', value: 'step', d: 'M3 9 L11 9 L11 3 L19 3' },
  { label: 'Straight', value: 'straight', d: 'M3 9 L19 3' },
];

// Label colours: the edge palette, plus a neutral default that follows the theme.
const LABEL_COLORS = [{ label: 'Default', value: '' }, ...COLORS];

const ARROWS = [
  { label: 'None', value: 'none', glyph: '—' },
  { label: 'End', value: 'end', glyph: '→' },
  { label: 'Both', value: 'both', glyph: '↔' },
];

const EdgeSettingsPanel: React.FC<EdgeSettingsPanelProps> = ({
  edge,
  onUpdate,
  onDelete,
  onClose,
}) => {
  const [label, setLabel] = useState('');

  // Sync local state with edge prop when selection changes
  useEffect(() => {
    if (edge) {
      setLabel(typeof edge.label === 'string' ? edge.label : '');
    }
    // Intentionally keyed on edge?.id only: local label edits must survive
    // edge-object updates and reset solely when a different edge is selected.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [edge?.id]);

  if (!edge) return null;

  const currentStroke = edge.style?.stroke || '#64748b';
  const currentDash = edge.style?.strokeDasharray || '0';
  // React Flow's default strokeWidth is 1 when the edge doesn't set one.
  const currentWidth = typeof edge.style?.strokeWidth === 'number' ? edge.style.strokeWidth : 1;
  const isAnimated = edge.animated || false;
  const currentType = edge.type || 'smoothstep';
  const currentLabelColor = (edge.data?.labelColor as string | undefined) ?? '';
  const hasStart = !!edge.markerStart;
  const hasEnd = !!edge.markerEnd;
  const currentArrow = hasStart && hasEnd ? 'both' : hasEnd || hasStart ? 'end' : 'none';

  const handleTypeChange = (type: string) => {
    onUpdate(edge.id, { type });
  };

  const handleArrowChange = (mode: string) => {
    const marker = { type: MarkerType.ArrowClosed, color: currentStroke };
    onUpdate(edge.id, {
      markerStart: mode === 'both' ? marker : undefined,
      markerEnd: mode === 'both' || mode === 'end' ? marker : undefined,
    });
  };

  // Handle typing without triggering updates (and snapshots) on every keystroke
  const handleLabelChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLabel(e.target.value);
  };

  // Commit changes when focus is lost or Enter is pressed
  const submitLabel = () => {
    if (label !== edge.label) {
      onUpdate(edge.id, { label: label });
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      submitLabel();
      (e.target as HTMLInputElement).blur();
    }
  };

  const handleColorChange = (color: string) => {
    onUpdate(edge.id, {
      style: { ...edge.style, stroke: color },
      markerEnd: edge.markerEnd ? { ...(edge.markerEnd as EdgeMarker), color: color } : undefined,
      markerStart: edge.markerStart ? { ...(edge.markerStart as EdgeMarker), color: color } : undefined,
    });
  };

  const handleLabelColorChange = (color: string) => {
    onUpdate(edge.id, { data: { ...edge.data, labelColor: color || undefined } });
  };

  const handleStyleChange = (dash: string) => {
    onUpdate(edge.id, {
      style: { ...edge.style, strokeDasharray: dash },
    });
  };

  const handleWidthChange = (width: number) => {
    onUpdate(edge.id, {
      style: { ...edge.style, strokeWidth: width },
    });
  };

  const toggleAnimation = () => {
    onUpdate(edge.id, { animated: !isAnimated });
  };

  return (
    <div className="absolute top-20 left-4 z-20 w-64 rounded-xl shadow-2xl border backdrop-blur-md animate-in slide-in-from-left-2 transition-colors bg-card/90 border-border dark:bg-card/90 dark:border-border">
      <div className="flex items-center justify-between p-3 border-b border-border dark:border-border">
        <h3 className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
          Edge Settings
        </h3>
        <button
          onClick={onClose}
          className="text-muted-foreground hover:text-muted-foreground dark:hover:text-foreground"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="p-3 space-y-4">
        {/* Label */}
        <div>
          <label className="block text-xs font-medium mb-1.5 text-foreground dark:text-muted-foreground">
            Label
          </label>
          <input
            type="text"
            value={label}
            onChange={handleLabelChange}
            onBlur={submitLabel}
            onKeyDown={handleKeyDown}
            placeholder="e.g. JSON, Auth"
            className="w-full text-sm px-2 py-1.5 rounded border focus:outline-none focus:ring-2 focus:ring-ring bg-muted border-border dark:bg-card dark:border-border text-foreground dark:text-foreground placeholder:text-muted-foreground"
          />
        </div>

        {/* Path type */}
        <div>
          <label className="block text-xs font-medium mb-1.5 text-foreground dark:text-muted-foreground">
            Type
          </label>
          <div className="flex bg-muted dark:bg-card p-1 rounded-lg gap-0.5">
            {TYPES.map((t) => (
              <Tooltip key={t.value} content={t.label} wrapperClassName="flex-1">
                <button
                  onClick={() => handleTypeChange(t.value)}
                  className={`w-full h-7 rounded flex items-center justify-center transition-all ${currentType === t.value ? 'bg-card shadow text-foreground dark:bg-muted' : 'text-muted-foreground hover:text-foreground'}`}
                  aria-label={`${t.label} edge`}
                >
                  <svg viewBox="0 0 22 12" className="w-6 h-3" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                    <path d={t.d} />
                  </svg>
                </button>
              </Tooltip>
            ))}
          </div>
        </div>

        {/* Style & Animation */}
        <div className="flex gap-2">
          <div className="flex-1">
            <label className="block text-xs font-medium mb-1.5 text-foreground dark:text-muted-foreground">
              Style
            </label>
            <div className="flex bg-muted dark:bg-card p-1 rounded-lg">
              {STYLES.map((s) => (
                <Tooltip key={s.label} content={s.label} wrapperClassName="flex-1">
                  <button
                    onClick={() => handleStyleChange(s.value)}
                    className={`w-full h-6 rounded flex items-center justify-center transition-all ${currentDash === s.value ? 'bg-card shadow text-foreground dark:bg-muted dark:text-foreground' : 'text-muted-foreground hover:text-muted-foreground dark:text-muted-foreground dark:hover:text-muted-foreground'}`}
                    aria-label={s.label}
                  >
                  <div
                    className="w-4 mx-auto h-0.5 bg-current"
                    style={{
                      borderTop:
                        s.label === 'Solid'
                          ? '2px solid'
                          : s.label === 'Dashed'
                            ? '2px dashed'
                            : '2px dotted',
                      borderBottom: 'none',
                      background: 'transparent',
                    }}
                  />
                  </button>
                </Tooltip>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium mb-1.5 text-foreground dark:text-muted-foreground">
              Flow
            </label>
            <Tooltip content="Toggle Animation">
              <button
                onClick={toggleAnimation}
                className={`h-8 w-8 flex items-center justify-center rounded-md border transition-all ${isAnimated ? 'bg-primary text-primary-foreground border-primary' : 'bg-card border-border text-muted-foreground hover:border-border dark:bg-card dark:border-border'}`}
                aria-label="Toggle Animation"
              >
                {isAnimated ? (
                  <Pause className="w-3 h-3" />
                ) : (
                  <Play className="w-3 h-3 fill-current" />
                )}
              </button>
            </Tooltip>
          </div>
        </div>

        {/* Width */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className="block text-xs font-medium text-foreground dark:text-muted-foreground">
              Width
            </label>
            <span className="text-xs text-muted-foreground tabular-nums">{currentWidth}px</span>
          </div>
          <RangeSlider
            value={currentWidth}
            onValueChange={handleWidthChange}
            min={1}
            max={8}
            step={1}
            aria-label="Edge width"
            formatValueText={(v) => `${v}px`}
            className="h-8"
          />
        </div>

        {/* Arrowheads */}
        <div>
          <label className="block text-xs font-medium mb-1.5 text-foreground dark:text-muted-foreground">
            Arrows
          </label>
          <div className="flex bg-muted dark:bg-card p-1 rounded-lg gap-0.5">
            {ARROWS.map((a) => (
              <Tooltip key={a.value} content={`${a.label} arrowheads`} wrapperClassName="flex-1">
                <button
                  onClick={() => handleArrowChange(a.value)}
                  className={`w-full h-7 rounded flex items-center justify-center text-sm transition-all ${currentArrow === a.value ? 'bg-card shadow text-foreground dark:bg-muted' : 'text-muted-foreground hover:text-foreground'}`}
                  aria-label={`${a.label} arrowheads`}
                >
                  {a.glyph}
                </button>
              </Tooltip>
            ))}
          </div>
        </div>

        {/* Colors */}
        <div>
          <label className="block text-xs font-medium mb-1.5 text-foreground dark:text-muted-foreground">
            Color
          </label>
          <div className="flex flex-wrap gap-2">
            {COLORS.map((c) => (
              <Tooltip key={c.value} content={c.label}>
                <button
                  onClick={() => handleColorChange(c.value)}
                  className={`w-6 h-6 rounded-full border-2 transition-transform hover:scale-110 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-offset-white dark:focus:ring-offset-slate-900 focus:ring-border ${currentStroke === c.value ? 'border-border dark:border-white' : 'border-transparent'}`}
                  style={{ backgroundColor: c.value }}
                  aria-label={c.label}
                />
              </Tooltip>
            ))}
          </div>
        </div>

        {/* Label color — only meaningful once the edge has a label */}
        {label.trim() && (
          <div>
            <label className="block text-xs font-medium mb-1.5 text-foreground dark:text-muted-foreground">
              Label color
            </label>
            <div className="flex flex-wrap gap-2">
              {LABEL_COLORS.map((c) => (
                <Tooltip key={c.value || 'default'} content={c.label}>
                  <button
                    onClick={() => handleLabelColorChange(c.value)}
                    className={`w-6 h-6 rounded-full border-2 transition-transform hover:scale-110 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-offset-white dark:focus:ring-offset-slate-900 focus:ring-border ${
                      currentLabelColor === c.value ? 'border-border dark:border-white' : 'border-transparent'
                    }`}
                    style={{
                      background: c.value
                        ? c.value
                        : 'linear-gradient(135deg, #e2e8f0 50%, #1e293b 50%)',
                    }}
                    aria-label={`${c.label} label`}
                  />
                </Tooltip>
              ))}
            </div>
          </div>
        )}

        {/* Delete */}
        <button
          onClick={() => onDelete(edge.id)}
          className="w-full flex items-center justify-center gap-2 text-xs font-medium text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 p-2 rounded-md transition-colors"
        >
          <Trash2 className="w-3 h-3" />
          Delete Connection
        </button>
      </div>
    </div>
  );
};

export default EdgeSettingsPanel;

